import React from 'react';
import { ContactWithDetails, ContactSearchParams } from './types';
import { useCategories } from '../category';
import { contactsApi } from './services/contactApi';
import { 
  PagerBar, 
  SearchListBar,
  SearchImportBar,
  ImportResultModal,
  SearchCriterion,
  SearchableField,
  SortBar,
  SortOption,
  ListItemSection,
  formatCategoryName,
  formatPhoneNumber,
  formatEmail,
  formatAddress,
  ListItemDisplayData
} from '../../../components/common';
import { LoadingBar } from '../../../components/common/LoadingBar';
import '../../../components/common/CRMList.css';

interface ContactListProps {
  contacts: ContactWithDetails[];
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  selectedContactId?: number;
  onSelectContact: (contact: ContactWithDetails) => void;
  onDeleteContact: (contact: ContactWithDetails) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onCreateNew: () => void;
  onSearch?: (searchParams: ContactSearchParams) => void;
  onImportComplete?: () => void;
  globalLoading?: boolean;
  loadingMessage?: string;
}

const ContactList: React.FC<ContactListProps> = ({
  contacts,
  error,
  pagination,
  selectedContactId,
  onSelectContact,
  onDeleteContact,
  onPageChange,
  onLimitChange,
  onCreateNew,
  onSearch,
  onImportComplete,
  globalLoading = false,
  loadingMessage = 'Loading...'
}) => {
  const { categories } = useCategories();
  
  // Search state for search and sort functionality
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedCriteria, setSelectedCriteria] = React.useState<SearchCriterion[]>([]);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [importLoading, setImportLoading] = React.useState(false);
  const [importResult, setImportResult] = React.useState<{ processed: number; notProcessed: Array<{ row: number; data: string; reason: string }> } | null>(null);
  const [currentSorts, setCurrentSorts] = React.useState<SortOption[]>([
    {
      field: 'dateUpdated',
      direction: 'desc',
      label: 'Updated Date'
    }
  ]);
  
  // Searchable fields configuration for new SearchListBar
  const searchableFields: SearchableField[] = [
    { key: 'firstName', label: 'First Name', type: 'text' },
    { key: 'lastName', label: 'Last Name', type: 'text' },
    { key: 'businessName', label: 'Business Name', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'phone', label: 'Phone', type: 'text' },
    { key: 'address', label: 'Address', type: 'text' },
    { key: 'contactTypeName', label: 'Contact Type', type: 'text' }
  ];
  
  // Sort column configuration - must match server valid sort fields
  const sortColumns = [
    { field: 'lastName', label: 'Last Name' },
    { field: 'businessName', label: 'Business Name' },
    { field: 'contactTypeName', label: 'Contact Type' },
    { field: 'dateUpdated', label: 'Updated Date' }
  ];
  
  // Handle search value changes (debounced)
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onSearch) {
        if (selectedCriteria.length > 0) {
          // Multiple field search (OR logic)
          onSearch({
            search: searchValue,
            fields: selectedCriteria.map(c => c.field),
            page: 1 // Reset to first page on search
          });
        } else {
          // Global search across all fields
          onSearch({
            search: searchValue,
            fields: undefined,
            page: 1 // Reset to first page on search
          });
        }
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, selectedCriteria, onSearch]);

  // Handle criteria changes
  const handleCriteriaChange = (criteria: SearchCriterion[]) => {
    setSelectedCriteria(criteria);
    
    // Update search params based on criteria
    if (onSearch) {
      if (criteria.length > 0) {
        onSearch({
          search: searchValue,
          fields: criteria.map(c => c.field),
          page: 1
        });
      } else {
        onSearch({
          search: searchValue,
          fields: undefined,
          page: 1
        });
      }
    }
  };
  
  // Handle sort changes
  const handleSortsChange = (sorts: SortOption[]) => {
    setCurrentSorts(sorts);
    
    if (onSearch) {
      if (sorts.length > 0) {
        // Convert sorts to the new format
        const sortArray = sorts.map(sort => ({
          field: sort.field as 'lastName' | 'businessName' | 'contactTypeName' | 'dateUpdated',
          direction: sort.direction as 'asc' | 'desc'
        }));
        
        onSearch({
          sorts: sortArray,
          page: 1 // Reset to first page when sorting changes
        });
      } else {
        // Clear sorts if none selected
        onSearch({
          sorts: undefined,
          page: 1
        });
      }
    }
  };

  const handleExport = React.useCallback(async () => {
    setExportLoading(true);
    try {
      const params: Record<string, string | number | undefined> = {};
      if (searchValue.trim()) params.search = searchValue.trim();
      if (selectedCriteria.length > 0) {
        params.fields = JSON.stringify(selectedCriteria.map(c => c.field));
      }
      if (currentSorts.length > 0) {
        params.sorts = JSON.stringify(currentSorts.map(s => ({ field: s.field, direction: s.direction })));
      }
      const blob = await contactsApi.exportCsv(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contacts.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExportLoading(false);
    }
  }, [searchValue, selectedCriteria, currentSorts]);

  const handleImport = React.useCallback(async (file: File) => {
    setImportLoading(true);
    setImportResult(null);
    try {
      const result = await contactsApi.importCsv(file);
      setImportResult(result);
      onImportComplete?.();
    } catch (err) {
      console.error('Import failed:', err);
      setImportResult({ processed: 0, notProcessed: [{ row: 0, data: file.name, reason: err instanceof Error ? err.message : 'Import failed' }] });
    } finally {
      setImportLoading(false);
    }
  }, [onImportComplete]);

  const handleCloseImportResult = React.useCallback(() => {
    setImportResult(null);
  }, []);

  // Handle page changes - use the prop handlers from App.tsx
  const handlePageChange = (page: number) => {
    onPageChange(page);
  };

  // Handle items per page changes - use the prop handlers from App.tsx
  const handleLimitChange = (limit: number) => {
    onLimitChange(limit);
  };
  
  // Unified display data function for ContactList
  const getDisplayData = (contact: ContactWithDetails): ListItemDisplayData => ({
    title: (contact.firstName || contact.lastName) 
      ? `${contact.firstName} ${contact.lastName}`.trim()
      : contact.businessName || 'Unnamed Contact',
    category: formatCategoryName(contact.typeId, categories),
    fields: [
      ...(contact.businessName && (contact.firstName || contact.lastName) ? [{
        name: 'business',
        value: contact.businessName
      }] : []),
      {
        name: 'phone',
        value: formatPhoneNumber(contact.primaryPhone)
      },
      {
        name: 'email',
        value: formatEmail(contact.primaryEmail)
      },
      {
        name: 'address',
        value: contact.primaryAddress ? formatAddress(contact.primaryAddress) : 'No address'
      }
    ]
  });

  if (error) {
    return <div className="app-error-message">{error}</div>;
  }

  return (
    <>
      <div className="list">
      <div className="list-header">
        <h3>Contacts ({pagination.total})</h3>
        <div className="list-header-actions">
          <SearchImportBar
            onImport={handleImport}
            loading={importLoading}
            title="Import contacts from CSV"
          />
          <button
            className="app-btn app-btn-primary list-btn-add-item"
            onClick={onCreateNew}
            title="Add New Contact"
          >
            +
          </button>
        </div>
      </div>
      
      {/* Search Bar */}
      <SearchListBar
        searchableFields={searchableFields}
        searchValue={searchValue}
        selectedCriteria={selectedCriteria}
        onSearchChange={setSearchValue}
        onCriteriaChange={handleCriteriaChange}
        placeholder="Search contacts..."
        onExport={handleExport}
        exportLoading={exportLoading}
      />
      
      {/* Sort Bar */}
      <SortBar
        availableColumns={sortColumns}
        currentSorts={currentSorts}
        onSortsChange={handleSortsChange}
        defaultSort={{
          field: 'dateUpdated',
          direction: 'desc',
          label: 'Updated Date'
        }}
      />

      <ListItemSection
        items={contacts}
        selectedItemId={selectedContactId}
        onSelectItem={onSelectContact}
        onDeleteItem={onDeleteContact}
        deleteConfirmMessage={(contact) => 
          `Are you sure you want to delete ${contact.firstName} ${contact.lastName}? This action cannot be undone.`
        }
        loading={false}
        loadingMessage=""
        emptyMessage="No contacts found. Try adjusting your search criteria."
        getDisplayData={getDisplayData}
      />

          {/* Bottom PagerBar */}
          <PagerBar
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            itemsPerPage={pagination.limit}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleLimitChange}
          />
      </div>
      
      {/* Global Loading Bar */}
      <LoadingBar 
        isLoading={globalLoading} 
        message={loadingMessage}
      />

      {importResult && (
        <ImportResultModal
          isOpen={!!importResult}
          processed={importResult.processed}
          notProcessed={importResult.notProcessed}
          onClose={handleCloseImportResult}
        />
      )}
    </>
  );
};

export default ContactList;
